'use client';

import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import { MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { DistanceCalculatorService } from '@/hotdesk-ui/services/distance.calculator.service';
import { GoogleMapsService } from '@/hotdesk-ui/services/google-map.service';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { formatAddressForDisplay, splitAddressClauses } from '@/hotdesk-ui/helpers/location.helper';
import LocationMapCard from './LocationMapCard';
import styles from './LocationSection.module.scss';
export interface LocationSectionProps {
  listing?: Record<string, any>;
  workspace?: Record<string, any>;
  cardItem?: MyListing;
  cardType?: 'workspace' | 'listing';
  cardUrl?: string;
}
const LocationSection: FC<LocationSectionProps> = ({
  listing,
  workspace,
  cardItem,
  cardType,
  cardUrl
}) => {
  const t = useTranslations('workspace-details');
  const [mapUrl, setMapUrl] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [address, setAddress] = useState('');
  const [distance, setDistance] = useState<number | null>(null);
  const [coords, setCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  useEffect(() => {
    const item = workspace || listing;
    if (!item) return;
    const addr = workspace ? item?.metaData?.detailedLocation?.address : item?.workspace?.metaData?.detailedLocation?.address;
    setAddress(formatAddressForDisplay(addr || ''));
    const lat = item?.location?.coordinates?.[1];
    const lng = item?.location?.coordinates?.[0];
    if (!lat || !lng) return;
    setCoords({
      lat,
      lng
    });
    setGoogleMapsUrl(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
    const googleMapService = new GoogleMapsService();
    googleMapService.getStaticMapUrl(lat, lng, 885, 14).then(setMapUrl);
    const distanceService = new DistanceCalculatorService();
    distanceService.calculateDistanceFromCurrentLocation(lat, lng).then(({
      success,
      distance: d
    }) => {
      if (success) setDistance(d);
    });
  }, [listing, workspace]);
  if (!mapUrl) return null;
  const addressClauses = splitAddressClauses(address);
  return <section className={styles.section} data-sentry-component="LocationSection" data-sentry-source-file="LocationSection.tsx">
            <div className={styles.header}>
                <h2 className={styles.heading}>
                    {t('location_title')}
                    {distance !== null && <>
                            <span className={styles.separator} aria-hidden="true">
                                --
                            </span>
                            <span className={styles.distance}>
                                {distance.toFixed(1)} {t('KM')} {t('away')}
                            </span>
                        </>}
                </h2>
            </div>
            {addressClauses.length > 0 && <p className={styles.address}>
                    <span className={styles.addressIcon} aria-hidden="true">
                        <MapPin size={18} strokeWidth={2} />
                    </span>
                    <span className={styles.addressText} dir="auto">
                        {addressClauses.map((clause, index) => <span key={index} className={styles.addressClause}>
                                {index > 0 && ', '}
                                <bdi>{clause}</bdi>
                            </span>)}
                    </span>
                </p>}
            <div className={styles.mapContainer}>
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className={styles.mapLink} aria-label="Open in Google Maps">
                    <Image src={mapUrl} fill alt="Location map" sizes="(max-width: 767px) 100vw, 834px" style={{
          objectFit: 'cover'
        }} data-sentry-element="Image" data-sentry-source-file="LocationSection.tsx" />
                </a>
                <div className={styles.pinStack}>
                    {cardItem && cardType && cardUrl && coords && <div className={styles.cardOverlay}>
                            <LocationMapCard item={cardItem} type={cardType} url={cardUrl} lat={coords.lat} lng={coords.lng} />
                        </div>}
                    <Image src="/assets/icons/map-icon.svg" width={56} height={56} alt="Map pin" className={styles.pin} data-sentry-element="Image" data-sentry-source-file="LocationSection.tsx" />
                </div>
            </div>
        </section>;
};
export default LocationSection;